import { clsx, type TypedPick } from "@plans/appkit";
import { controlSizeClassMap } from "~/ui/constants";
import type { ControlSize, ResponsiveProperty } from "~/ui/types";
import { useResponsiveSize } from "~/ui/useResponsiveSize";
import { InfoTooltip } from "./InfoTooltip";
import { Markdown } from "./Markdown";

type FormControlBaseProps = {
	/**
	 * Name of the control;
	 */
	name: string;
	/**
	 * Responsive size of the control. Pass either a single `ControlSize` to be
	 * used for all screen widths, or a tuple of `ControlSize` where the first
	 * element is the standard size, the second element the `ControlSize` at the
	 * first breakpoint, the third element the `ControlSize` at the second
	 * breakpoint, and so on. Defaults to "medium",
	 */
	size?: ResponsiveProperty<ControlSize>;
	/**
	 * Label to display above the control. Will be wrapped inside a <label />.
	 */
	label?: React.ReactNode;
	/**
	 * ClassName to apply to the label.
	 */
	labelClassName?: string;
	/**
	 * `for` attribute to apply to the label if set.
	 */
	labelHtmlFor?: string;
	/**
	 * Optional control rendered at the trailing edge of the label row (e.g. a
	 * lock/reset affordance owning the field). Kept in the label row rather than
	 * given a row of its own so toggling it does not shift the label — and so it
	 * lines up with the same affordance on a read-only rendering of the field.
	 */
	labelAction?: React.ReactNode;
	/**
	 * Error message to display below the control if `isInvalid` is `true`.
	 */
	errorMessage?: string;
	/**
	 * Optional hint to display below the control.
	 */
	hint?: string;
	/**
	 * Optional prefix to display inline after the control.
	 */
	prefix?: React.ReactNode;
	/**
	 * Optional icon to display absolutely inside the control. Whatever is passed
	 * is centered inside a 20x20px box
	 */
	prefixIcon?: React.ReactNode;
	/**
	 * Optional icon to display absolutely inside the control. Whatever is passed
	 * is centered inside a 20x20px box.
	 */
	suffixIcon?: React.ReactNode;
	/**
	 * Tooltip to display next to the label. This will be parsed as Markdown if
	 * a string is passed. WARNING: This is a potential security risk, so never
	 * set tooltips from untrusted sources
	 */
	tooltip?: React.ReactNode;
	/**
	 * Whether or not the control is invalid. If truthy, will display an outline
	 * with the error tint color as well as display `errorMessage` if set.
	 */
	isInvalid?: boolean;
	/**
	 * Orientation of the control. Defaults to "vertical".
	 */
	orientation?: "vertical" | "horizontal";
	/**
	 * Whether or not the control is disabled.
	 */
	isDisabled?: boolean;
	/**
	 * Whether or not the control is mandatory to fill in.
	 */
	isRequired?: boolean;
	/**
	 * className to apply to the root element.
	 */
	className?: string;
};

export type SuffixProps =
	| {
			/** Optional suffix to display inline before the control. */
			suffix?: React.ReactNode;
			insetSuffix?: never;
	  }
	| {
			/** Optional content to display inside the control at the right edge. */
			suffix?: never;
			insetSuffix?: React.ReactNode;
	  };

/** All keys across every member of a union — distributes so `K` below can be
 * any member's key, not just ones shared by all of them. */
type AllKeys<T> = T extends unknown ? keyof T : never;

export type DistributiveOmit<T, K extends AllKeys<T>> = T extends unknown
	? Omit<T, K>
	: never;

export type FormControlProps = FormControlBaseProps & SuffixProps;

export function formControlSuffixProps(
	suffix: React.ReactNode | undefined,
	insetSuffix: React.ReactNode | undefined,
	defaultSuffix?: React.ReactNode,
): SuffixProps {
	if (suffix === undefined) {
		if (insetSuffix === undefined) {
			return defaultSuffix === undefined ? {} : { suffix: defaultSuffix };
		}
		return { insetSuffix };
	}
	return { suffix };
}

/**
 * Padding the value needs so it clears the trailing adornments.
 *
 * The widths are constants, as they have to be: the unit is arbitrary text, and
 * measuring it would mean a layout pass per keystroke. 3rem covers a unit up to
 * about six characters at the sizes these controls use; the icon adds its own
 * 1.25rem box plus the 0.25rem gap.
 */
export function trailingPaddingClassName(
	hasSuffixIcon: boolean,
	hasInsetSuffix: boolean,
): string | false {
	if (hasSuffixIcon && hasInsetSuffix) {
		return "[&>input]:pe-[4.5rem] [&>select]:pe-[4.5rem]";
	}
	if (hasInsetSuffix) {
		return "[&>input]:pe-12 [&>select]:pe-12";
	}
	if (hasSuffixIcon) {
		return "[&>input]:pe-8 [&>select]:pe-8";
	}
	return false;
}

export function FormControl({
	children,
	className,
	size = "medium",
	orientation = "vertical",
	labelClassName,
	label,
	name,
	labelHtmlFor,
	labelAction,
	errorMessage,
	hint,
	prefix,
	prefixIcon,
	suffix,
	suffixIcon,
	insetSuffix,
	tooltip,
	isInvalid,
	isRequired,
	isDisabled,
}: React.PropsWithChildren<FormControlProps>) {
	const sizeClassList = useResponsiveSize(size, controlSizeClassMap);
	const controlLabel = (
		<FormControlLabel
			label={label}
			labelHtmlFor={labelHtmlFor}
			isRequired={isRequired}
			isInvalid={isInvalid}
			tooltip={tooltip}
			orientation={orientation}
			className={clsx(
				sizeClassList,
				labelClassName,
				"min-h-0 gap-0 p-0",
				// Sharing the row with an action means the label has to give way:
				// `FormControlLabel` is `flex-none` by default, so a long label would
				// otherwise push the action out of the container instead of wrapping.
				labelAction && "min-w-0 flex-1",
			)}
		/>
	);

	return (
		<span
			className={clsx(
				"flex",
				orientation === "vertical"
					? "max-w-[360px] flex-col gap-1.5 justify-self-stretch"
					: "@md:flex-row @md:flex-wrap @md:items-center @md:gap-3 flex-1 flex-col gap-1.5",
				className,
			)}
		>
			{labelAction ? (
				<span className="flex min-w-0 items-center justify-between gap-2">
					{controlLabel}
					<span className="flex-none">{labelAction}</span>
				</span>
			) : (
				controlLabel
			)}
			<span className={clsx("flex flex-1 flex-col gap-1.5")}>
				<span className="flex flex-1">
					{prefix && (
						<span
							className={clsx(
								"border-background-medium inline-flex select-none items-center whitespace-nowrap rounded-s-md border border-e-0",
								sizeClassList,
								isDisabled
									? "bg-background-soft"
									: "bg-background-default [fieldset:disabled_&]:bg-background-soft",
							)}
						>
							{prefix}
						</span>
					)}
					<span
						className={clsx(
							"relative flex w-full [&:not(:first-child)>*]:rounded-s-none [&:not(:last-child)>*]:rounded-e-none",
							prefixIcon && "[&>input]:pl-8 [&>select]:pl-8",
							// One ladder, not two rules: the trailing icon and the inset
							// unit share a strip, so the value has to clear whichever of
							// them is present — and both when they are.
							trailingPaddingClassName(
								Boolean(suffixIcon),
								Boolean(insetSuffix),
							),
						)}
					>
						{children}
						{prefixIcon && (
							<span
								className={clsx(
									"pointer-events-none absolute top-1/2 flex h-5 w-5 -translate-y-1/2 select-none items-center justify-center",
									size === "xs" ? "left-1" : "left-3",
								)}
							>
								{prefixIcon}
							</span>
						)}
						{(insetSuffix || suffixIcon) && (
							// One stack rather than two independently positioned spans: an
							// invalid field with a unit used to draw its error icon on top
							// of the unit, because both were anchored to the right edge.
							<span
								className={clsx(
									"pointer-events-none absolute top-1/2 flex -translate-y-1/2 select-none items-center gap-1",
									size === "xs" ? "right-1" : "right-3",
								)}
							>
								{insetSuffix}
								{suffixIcon && (
									<span className="flex h-5 w-5 items-center justify-center">
										{suffixIcon}
									</span>
								)}
							</span>
						)}
					</span>
					{suffix && (
						<span
							className={clsx(
								"border-background-medium inline-flex select-none items-center whitespace-nowrap rounded-e-md border border-s-0",
								sizeClassList,
								isDisabled
									? "bg-background-soft"
									: "bg-background-default [fieldset:disabled_&]:bg-background-soft",
							)}
						>
							{suffix}
						</span>
					)}
				</span>
				<FormControlMessage
					className={clsx(sizeClassList, "min-h-0 gap-0 p-0")}
					name={name}
					errorMessage={errorMessage}
					hint={hint}
					isInvalid={isInvalid}
				/>
			</span>
		</span>
	);
}

export function FormControlLabel({
	label,
	labelClassName,
	labelHtmlFor,
	isRequired,
	tooltip,
	className,
	isInvalid,
}: TypedPick<
	FormControlProps,
	| "label"
	| "labelHtmlFor"
	| "isRequired"
	| "tooltip"
	| "className"
	| "orientation"
	| "labelClassName"
	| "isInvalid"
>) {
	if (!label) {
		return null;
	}

	return (
		<span
			className={clsx(
				"text-text-primary inline-flex flex-none select-none items-center gap-px text-sm",
				className,
			)}
		>
			<label
				htmlFor={labelHtmlFor}
				className={clsx(
					"text-text-secondary inline-flex items-center gap-[0.08em] font-semibold",
					labelHtmlFor && "cursor-pointer",
					labelClassName,
				)}
			>
				{label}
				{isRequired && (
					<span
						role="presentation"
						className={clsx(
							"leading-0 text-[1.1em]",
							isInvalid && "text-accent-error",
						)}
					>
						*
					</span>
				)}
			</label>
			{tooltip && (
				<InfoTooltip
					className="text-text-secondary"
					content={
						typeof tooltip === "string" ? (
							<Markdown>{tooltip}</Markdown>
						) : (
							tooltip
						)
					}
				/>
			)}
		</span>
	);
}

export function getAriaErrorMessageIdForControl(name: string) {
	return `form-control-error-message-${name}`;
}

export function FormControlErrorMessage({
	children,
	className,
	hidden,
	name,
}: TypedPick<FormControlProps, "className" | "name"> & {
	children: React.ReactNode;
	hidden?: boolean;
}) {
	const id = getAriaErrorMessageIdForControl(name);
	return (
		<span
			id={id}
			role="alert"
			data-cy={id}
			hidden={hidden}
			className={clsx(
				"text-accent-error ms-0.5 text-sm",
				className,
				!hidden && "block",
			)}
		>
			{children}
		</span>
	);
}

function FormControlHint({
	hint,
	className,
	hidden,
}: TypedPick<FormControlProps, "hint" | "className"> & {
	hidden?: boolean;
}) {
	return (
		<span
			hidden={hidden}
			className={clsx(
				"text-text-secondary ms-0.5 text-sm",
				className,
				!hidden && "block",
			)}
		>
			{hint}
		</span>
	);
}

export function FormControlMessage({
	errorMessage,
	size,
	hint,
	isInvalid,
	className,
	name,
}: TypedPick<
	FormControlProps,
	"errorMessage" | "hint" | "isInvalid" | "className" | "name" | "size"
>) {
	const hasErrorMessage = Boolean(errorMessage);
	return (
		<>
			<FormControlErrorMessage
				hidden={!hasErrorMessage || !isInvalid}
				name={name}
				className={className}
			>
				{errorMessage}
			</FormControlErrorMessage>
			<FormControlHint
				hidden={Boolean(!hint || (errorMessage && isInvalid))}
				hint={hint}
				className={className}
			/>
		</>
	);
}
